import React, { useState, useEffect } from 'react';
import { IconButton, Icon, Select, MenuItem } from '@mui/material';
import MuiCloseIcon from '@mui/icons-material/Close';
import {
  ButtonContainer,
  CloseBtn,
  CloseIcon,
  Container,
  InputContainer,
  SelectWrap,
  ShareButton,
  ShareDialog,
  Subtitle,
  TitleRow,
} from './ShareModal.styles';
import { replace, first, get, last, uniq, flatten } from 'global/lib/lodash';
import { Alert } from '@mui/material';
import { copyToClipboard, addNewLinesToText, checkCanShare } from 'global/utils';
import SnackAlerts from 'global/components/snack-alerts';
import { getTradeName } from '../../../global/components/trade-select';
import { CenterLoader } from '../../../global/styles';
import sendData from 'redux/actions/sendData';

export default (props) => {
  const [error, setError] = useState();
  const [alerts, setAlerts] = useState();
  const [canShare, setCanShare] = useState(false);
  const [selectOpts, setSelectOpts] = useState([]);
  const [selected, setSelected] = useState();
  const [state, setState] = useState({
    loading: false,
    links: []
  })

  const title = props.title || 'Share PDF Link';

  const handleClose = () => {
    props.onClose();
  };

  const clearAlerts = () => {
    setAlerts();
  };

  const getLink = (lnk = selected) => {
    if (props.link) { return props.link }

    const isAll = lnk == 'all';
    const link = get(state, `links`, []).find(l => isAll ? get(l, 'customProperties.tradeType') == 'all' : get(l, 'customProperties.tradeTypeId') == lnk);
    return get(link, 'full7DayUrl');
  }

  const copyLink = () => {
    clearAlerts();
    const link = getLink();
    copyToClipboard(link);
    setAlerts({
      message: 'Link copied to clipboard.',
    });
  };

  const openLink = () => {
    window.open(getLink(), '_blank');
  };

  const copyToEmail = () => {
    //this is all necessary because the ? and & get stripped from the body
    const repl = {
      '\\?': '%3F',
      '\\&': '%26',
    };

    const emailText = `You've been assigned a new task, please review the pdf here: `;
    const fullText = props.emailText || 
`${emailText}${props.link ? '' : getLink()}

Thanks,
`;
    let txt = addNewLinesToText(fullText, '%0D%0A%0D%0A');
    Object.keys(repl).map((k) => {
      txt = replace(txt, new RegExp(k, 'g'), repl[k]);
    });
    const subject = props.isScorePdf ? `Assessment Score PDF` : title;
    const emails = uniq(flatten((props.tasks || []).map(t => get(t, 'assignees', []).map(a => a.email).filter(Boolean)))).join(',');
    let url = `mailto:${emails || ''}?subject=${subject}&body=${txt}`;
    window.open(url, '_blank');
  };

  const share = async () => {
    try {
      await navigator.share({ title, url: getLink() });
    } catch (err) {
      console.warn(err);
    }
  };

  const changeSelected = async e => {
    try {
      const val = e.target.value;
      setSelected(val);

      const hasLink = getLink(val);

      if (!hasLink) {
        setState(s => ({ ...s, loading: true }));

        const opts = [...selectOpts];
        opts.shift()

        const relationships = {
          tasks: {
            data: props.tasks.map(t => ({ id: t.id, type: 'tasks' })),
          },
          tradeTypes: {
            data: opts.filter(f => f.id == val).map(t => ({ id: t.id, type: 'trade-types' }))
          }
        }

        const resp = await sendData({
          url: `/messages` + (state.id ? `/${state.id}` : ''),
          method: state.id ? 'patch' : 'post',
          data: {
            data: {
              type: 'messages',
              id: state.id,
              attributes: {
                title: 'New Task',
                body: 'Test',
                message_type: 'task_share',
                model_type: 'task',
                realm_id: get(last(props.tasks), 'realm.id'),
                status: 1,
                trade_type: val === 'all' ? 'all' : get(selectOpts.find(o => o.id == val), 'name'),
                trade_type_id: val === 'all' ? null : val,
                // separate_by_trades: val !== 'all',
              },
              relationships,
            }
          }
        }, { normalize: { build: ['messages'] }, params: { include: 'media' } });

        const res = last(resp);
        const links = get(res, 'media', []);

        setState(s => ({ ...s, links, id: res.id }))
      }
    } catch (err) {
      setAlerts({ message: err.toString(), severity: 'error' });
    } finally {
      setState(s => ({ ...s, loading: false }));
    }
  }

  useEffect(() => {
    if (props.open) {
      const canShare = checkCanShare();
      setCanShare(canShare);

      //#region set the dropdown options
      if (!props.link) {
        const tradeOptions = [];
        let total = 0;
        if (props.tasks) {
          props.tasks.map(t => {
            total++;
            (t.tradeTypes || []).map(trade => {
              const hasTrade = tradeOptions.find(t => t.id == trade.id);
              if (hasTrade) {
                hasTrade.total++;
              } else {
                tradeOptions.push({ ...trade, total: 1 });
              }
            })
          })
        }
        const allOpts = [{ name: 'All Trades', id: 'all', total }, ...tradeOptions];
        setSelectOpts(allOpts);
        changeSelected({ target: { value: first(allOpts).id }});
      }
      //#endregion

      setTimeout(() => {
        if (canShare) {
          try {
            document.getElementById('ShareBtn').click();
          } catch (err) {
            console.warn(err);
          }
        }
      }, 500);
    } else {
      setState(s => ({ ...s, links: [], id: undefined }))
    }
  }, [props.open]);

  return (
    <ShareDialog disableEscapeKeyDown open={props.open} onClose={handleClose}>
      <Container>
        {error && (
          <Alert
            severity="error"
            action={
              <IconButton
                aria-label="close"
                color="inherit"
                size="small"
                onClick={() => {
                  setError();
                }}
              >
                <MuiCloseIcon fontSize="inherit" />
              </IconButton>
            }
          >
            {error}
          </Alert>
        )}

        <CloseIcon onClick={handleClose} size="large">
          <Icon>close</Icon>
        </CloseIcon>
        <TitleRow>
          <Subtitle variant="h5">
            {title}
          </Subtitle>
        </TitleRow>

        <InputContainer>
          <ButtonContainer>
            {!!state.loading && <CenterLoader size={60} />}
            {!state.loading && <>
              {!props.link && <SelectWrap>
                <Select size="small" fullWidth variant="outlined" value={selected} onChange={changeSelected}>
                  {selectOpts.map(s => <MenuItem value={s.id}>{`${getTradeName(s)} (${s.total})`}</MenuItem>)}
                </Select>
              </SelectWrap>}

              <ShareButton onClick={openLink} variant="outlined" color="secondary">
                VIEW PDF
              </ShareButton>

              <ShareButton onClick={copyLink} startIcon={<Icon>filter_none</Icon>} variant="contained" color="secondary">
                COPY LINK
              </ShareButton>

              <ShareButton onClick={copyToEmail} startIcon={<Icon>mail</Icon>} variant="contained" color="secondary">
                COPY TO EMAIL
              </ShareButton>

              {canShare && (
                <ShareButton onClick={share} startIcon={<Icon>share</Icon>} variant="contained" color="secondary" id="ShareBtn">
                  SHARE
                </ShareButton>
              )}
            </>}
          </ButtonContainer>
        </InputContainer>
      </Container>
      <SnackAlerts alerts={alerts} setAlerts={setAlerts} />
    </ShareDialog>
  );
};
